/* eslint-disable guard-for-in */
import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { FlushDTypeData } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, throwError } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { DTypeMasterdataDialogConfig } from './dtype-masterdata-dialog.types';
import { MasterData, MasterTypology } from '../../core/master-data/master-data.types';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';

@Component({
    selector: 'app-dtype-masterdata-dialog',
    templateUrl: './dtype-masterdata-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .dtype-masterdata-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeMasterDataDialogComponent implements OnInit {

    actionFn = {
        save: this.save.bind(this),
        cancel: this.closeDialog.bind(this),
        clear: this.clear.bind(this),
    };
    dTypeForm: DTypeForm;
    hasChanges: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            item: any;
            dType: MasterData;
            config: DTypeMasterdataDialogConfig;
            update: boolean;
            readonly: boolean;
        },
        private dialogsService: DialogsService,
        public dialogRef: MatDialogRef<DTypeMasterDataDialogComponent>,
        private dTypeService: DTypeService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private localize: LocalizePipe
    ) { }

    get actions() {
        return Object.values(this.data.config.actions);
    }

    ngOnInit() {
        for (const actionKey in this.data.config.actions) {
            this.data.config.actions[actionKey]['key'] = actionKey;
        }
        this.dTypeForm = this.dTypeService.getDtypeFormFromDtype(
            this.data.dType,
            this.data.item,
            {
                readonly: this.data.readonly,
                isUpdate: this.data.update
            }
        );
        if (this.data.dType.typology === MasterTypology.LOCATION) {
            this.dTypeForm.fields['name'].hidden = true;
            if (this.dTypeForm.control.controls['address'].value) {
                this.dTypeForm.control.get('address').value['newName'] = this.dTypeForm.control.controls['name'].value;
            }
        }

        let control: AbstractControl;
        if (this.data.update) {
            for (const index of this.data.dType.index) {
                if (index.unique) {
                    for (const col of index.column) {
                        control = this.dTypeForm.control.get(col) as any;
                        if (control) {
                            control.disable();
                        }
                    }
                }
            }
        }

        this.dTypeForm.control.valueChanges.subscribe(() => {
            if (this.data.dType.typology === MasterTypology.LOCATION) {
                this.dTypeForm.control.get('name').setValue(this.dTypeForm.control.get('address').value['newName'], { emitEvent: false });
            }
            this.hasChanges = true;
        });
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('saveDataItemConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                const form = this.dTypeForm.control;
                form.markAllAsTouched();
                if (form.valid) {
                    if (this.data.dType.typology === MasterTypology.LOCATION) {
                        delete form.get('address').value['newName'];
                    }
                    const body: FlushDTypeData = { uri: this.data.dType.uri, data: form.getRawValue() };
                    this.dTypeService.flushDTypeDataItem(body).pipe(
                        catchError((error) => {
                            console.error(error);
                            this.dialogsService.openErrorDialog({
                                message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                            });
                            return throwError(() => error);
                        })
                    ).subscribe((data) => {
                        if (data) {
                            if (typeof data === 'object' && 'isDuplicatePlaceId' in data && data['isDuplicatePlaceId'] === true) { //Aviso de direcciones con placeId duplicado
                                this.dialogsService.openErrorDialog({
                                    title: this.transloco.translate('WARNING'),
                                    message: this.transloco.translate('duplicatedPlaceId')
                                });
                            }
                            this.dialogRef.close(data);
                        } else {
                            this.dialogsService.openErrorDialog({
                                message: this.transloco.translate('dataItemCouldNotBeSaved')
                            });
                        }
                    });
                } else {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                }
            }
        });
    }

    async closeDialog() {
        if (this.hasChanges) {
            const closeMasterDataConfirm = await this.closeConfirmDialog(this.transloco.translate('warning'), this.transloco.translate('closeMasterDataConfirm'));
            if (closeMasterDataConfirm) {
                this.dialogRef.close();
            }
        } else {
            this.dialogRef.close();
        }
    }

    clear() {
        this.dTypeForm.control.reset();
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

    private closeConfirmDialog(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

}
